import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import ResultsView from "@/components/ResultsView";
import CustomerInfoForm from "@/components/CustomerInfoForm";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import SfpPanel from "@/components/SfpPanel";
import RenewalPanel from "@/components/RenewalPanel";
import AddonsPanel from "@/components/AddonsPanel";
import ManualModelsPanel from "@/components/ManualModelsPanel";
import NetworkingAccessPanel from "@/components/NetworkingAccessPanel";
import PricelistFreshnessBanner from "@/components/PricelistFreshnessBanner";
import QuoteChatRoom from "@/components/QuoteChatRoom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Building2, User, Award, Lock } from "lucide-react";

const CUSTOMER_TYPES = ["SMB", "Enterprise", "Government"];
const USE_CASES = ["Firewall", "SD-WAN", "SASE", "Zero Trust", "SOC"];
const SECURITY_LEVELS = ["Basic", "Advanced", "Enterprise"];
const DURATIONS = [1, 2, 3, 5];
const PARTNER_LEVELS = [
  { value: "none", label: "Aucun (prix public)", hw: 0, sw: 0 },
  { value: "Advocate", label: "Advocate", hw: 25, sw: 15 },
  { value: "Select", label: "Select", hw: 30, sw: 20 },
  { value: "Advanced", label: "Advanced", hw: 35, sw: 25 },
  { value: "Expert", label: "Expert", hw: 45, sw: 30 },
];
const CARRIERS = [
  { value: "none", label: "Aucun transport" },
  { value: "DHL", label: "DHL Express Worldwide" },
  { value: "UPS", label: "UPS Worldwide Expedited" },
  { value: "FedEx", label: "FedEx International Priority" },
];
const DESTINATION_ZONES = [
  { value: "Domestic", label: "Domestique (pays d'origine)", mult: 1.0 },
  { value: "Europe", label: "Europe", mult: 1.25 },
  { value: "Americas", label: "Amériques", mult: 1.75 },
  { value: "Asia-Pacific", label: "Asie-Pacifique", mult: 1.9 },
  { value: "Other", label: "Autre / Intercontinental", mult: 2.0 },
];

export default function Configurator() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    users: 150, sites: 3, customer_type: "Enterprise",
    use_case: "SD-WAN", security_level: "Advanced", license_years: 3,
    partner_level: "none",
    carrier: "none",
    destination_zone: "Europe",
    deployment_type: "physical", // "physical" | "vm"
  });
  const [mode, setMode] = useState("deployment"); // "deployment" | "renewal" | "solutions_only" | "manual"
  const [sizingMethod, setSizingMethod] = useState("users"); // "users" | "performance" (only used in deployment mode)
  const renewalMode = mode === "renewal";
  const solutionsOnlyMode = mode === "solutions_only";
  const manualMode = mode === "manual";
  const perfMode = mode === "deployment" && sizingMethod === "performance";
  const [renewalItems, setRenewalItems] = useState([]);
  const [perfSizing, setPerfSizing] = useState({});
  const [addons, setAddons] = useState([]);
  const [manualModels, setManualModels] = useState([]);
  const [resultsByMode, setResultsByMode] = useState({
    deployment: null,
    renewal: null,
    solutions_only: null,
    manual: null,
  });
  const result = resultsByMode[mode] || null;
  const setResult = (r) => setResultsByMode((s) => ({ ...s, [mode]: r }));
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  // Once the partner saves the BOM, we keep the resulting quote_id so the
  // « Demander une revue Fortinet » card on ResultsView can call
  // /api/quotes/{id}/review-request without forcing the partner to leave the
  // page and navigate to /quotes first.
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [assignedSe, setAssignedSe] = useState(null);
  const [chatQuoteId, setChatQuoteId] = useState(null);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [activePL, setActivePL] = useState(null);
  const [baseMeta, setBaseMeta] = useState(null);
  const [sfpQuantities, setSfpQuantities] = useState({});

  // Customer / partner metadata captured at the top of the Configurator so it
  // can be persisted with the quote AND used to pre-address the Outlook draft.
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    country: "",
    headquarters: "",
    branch: "",
    partner_name: "",
    generated_by: "",
    generated_by_email: "",
  });

  // Auto-fill partner_name + generated_by once the auth context resolves.
  // Only set defaults if the field is still untouched, so the partner can
  // override them at any time without us clobbering on every render.
  useEffect(() => {
    if (!user) return;
    setCustomerInfo((c) => ({
      ...c,
      partner_name: c.partner_name || user.company || "",
      generated_by: c.generated_by || user.full_name || user.email || "",
      generated_by_email: c.generated_by_email || user.email || "",
    }));
  }, [user?.company, user?.full_name, user?.email]);

  useEffect(() => {
    if (!user) { setActivePL(null); return; }
    api.get("/pricelist/active")
      .then((r) => setActivePL(r.data.active ? r.data : null))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    // Base price list freshness — visible to anyone (no auth) so newcomers
    // immediately see how recent the pricing is before they start a quote.
    api.get("/pricelist/base/meta")
      .then((r) => setBaseMeta(r.data))
      .catch(() => setBaseMeta(null));
  }, []);

  // Mirror the user's locked partner level into the form so every payload
  // sent to /api/configure carries it. Backend re-applies the lock anyway as
  // a defense in depth, but this keeps the UI consistent.
  useEffect(() => {
    if (user?.partner_level_locked && user?.partner_level) {
      setForm((f) => ({ ...f, partner_level: user.partner_level }));
    }
  }, [user?.partner_level_locked, user?.partner_level]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetCurrentMode = () => {
    if (result) {
      const ok = window.confirm(
        "Réinitialiser ce BOM ? Les saisies et le résultat actuel seront effacés (l'enregistrement éventuel reste dans Mes devis)."
      );
      if (!ok) return;
    }
    // Clear the result for this mode only
    setResultsByMode((s) => ({ ...s, [mode]: null }));
    if (mode === "renewal") {
      setRenewalItems([]);
    } else if (mode === "solutions_only") {
      setAddons([]);
    } else if (mode === "manual") {
      setManualModels([]);
      setAddons([]);
      setSfpQuantities({});
    } else {
      // deployment mode
      setAddons([]);
      setPerfSizing({});
      setSfpQuantities({});
      setSizingMethod("users");
      setForm((f) => ({
        ...f,
        users: 150, sites: 3,
        customer_type: "Enterprise",
        use_case: "SD-WAN",
        security_level: "Advanced",
        license_years: 3,
      }));
    }
    toast.success("BOM réinitialisé");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (renewalMode && renewalItems.length === 0) {
      toast.error("Ajoutez au moins un modèle au parc à renouveler.");
      return;
    }
    if (solutionsOnlyMode && addons.length === 0) {
      toast.error("Ajoutez au moins une solution complémentaire.");
      return;
    }
    if (manualMode && manualModels.length === 0) {
      toast.error("Ajoutez au moins un modèle FortiGate.");
      return;
    }
    setLoading(true);
    try {
      let payload;
      if (renewalMode) {
        payload = {
          renewal_mode: true,
          renewal_items: renewalItems
            .filter((r) => r.model_name && Number(r.qty) > 0)
            .map((r) => ({ model_name: r.model_name, qty: Number(r.qty) })),
          security_level: form.security_level,
          license_years: Number(form.license_years),
          customer_type: form.customer_type,
          partner_level: form.partner_level === "none" ? null : form.partner_level,
        };
      } else if (solutionsOnlyMode) {
        payload = {
          solutions_only_mode: true,
          customer_type: form.customer_type,
          security_level: form.security_level,
          license_years: Number(form.license_years),
          partner_level: form.partner_level === "none" ? null : form.partner_level,
          carrier: form.carrier === "none" ? null : form.carrier,
          destination_zone: form.carrier === "none" ? null : form.destination_zone,
          addons: addons.map((a) => ({
            sku: a.sku,
            quantity: Number(a.quantity),
            solution_id: a.solution_id,
            include_ps: !!a.include_ps,
          })),
        };
      } else if (manualMode) {
        const sfpList = Object.entries(sfpQuantities)
          .filter(([, q]) => Number(q) > 0)
          .map(([sku, quantity]) => ({ sku, quantity: Number(quantity) }));
        payload = {
          manual_mode: true,
          manual_models: manualModels.map((m) => ({
            model: m.model,
            quantity: Number(m.quantity) || 1,
            role: m.role || "Site",
          })),
          customer_type: form.customer_type,
          security_level: form.security_level,
          license_years: Number(form.license_years),
          partner_level: form.partner_level === "none" ? null : form.partner_level,
          carrier: form.carrier === "none" ? null : form.carrier,
          destination_zone: form.carrier === "none" ? null : form.destination_zone,
          sfp_modules: sfpList.length ? sfpList : null,
          addons: addons.length
            ? addons.map((a) => ({
                sku: a.sku,
                quantity: Number(a.quantity),
                solution_id: a.solution_id,
                include_ps: !!a.include_ps,
              }))
            : null,
        };
      } else {
        const sfpList = Object.entries(sfpQuantities)
          .filter(([, q]) => Number(q) > 0)
          .map(([sku, quantity]) => ({ sku, quantity: Number(quantity) }));
        const perfClean = Object.fromEntries(
          Object.entries(perfSizing || {}).filter(([, v]) => v != null && Number(v) > 0)
        );
        payload = {
          ...form,
          users: Number(form.users),
          sites: Number(form.sites),
          license_years: Number(form.license_years),
          partner_level: form.partner_level === "none" ? null : form.partner_level,
          carrier: form.carrier === "none" ? null : form.carrier,
          destination_zone: form.carrier === "none" ? null : form.destination_zone,
          sfp_modules: sfpList.length ? sfpList : null,
          performance_sizing: Object.keys(perfClean).length ? perfClean : null,
          addons: addons.length
            ? addons.map((a) => ({
                sku: a.sku,
                quantity: Number(a.quantity),
                solution_id: a.solution_id,
                include_ps: !!a.include_ps,
              }))
            : null,
        };
      }
      const r = await api.post("/configure", payload);
      setResult(r.data);
      // Fresh BOM = no saved quote yet, no review requested yet
      setSavedQuoteId(null);
      setReviewRequested(false);
      setAssignedSe(null);
      setTimeout(() => document.getElementById("results-section")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la génération");
    } finally {
      setLoading(false);
    }
  };

  const openSave = () => {
    if (!user) {
      toast.info("Connectez-vous pour enregistrer vos devis");
      nav("/login");
      return;
    }
    // Insistent guard: warn the partner if any "addon_auto" indispensable
    // recommendation is missing from the BOM about to be saved. Same logic
    // as the QuoteEditor edit-mode guard, applied at first save.
    const bomSkus = new Set((result?.bom || []).map((b) => b.sku));
    const missing = (result?.addon_recommendations || [])
      .filter((r) => r.kind === "addon_auto" && r.direct_sku)
      .filter((r) => !bomSkus.has(r.direct_sku));
    if (missing.length > 0) {
      const list = missing
        .map((r) => `• ${r.name}${r.direct_sku ? ` (${r.direct_sku})` : ""}`)
        .join("\n");
      const proceed = window.confirm(
        `⚠ Vous avez ignoré ${missing.length} add-on(s) indispensable(s) :\n\n${list}\n\nCes éléments ont été identifiés comme nécessaires pour le projet (cas d'usage / typologie client). Confirmer l'enregistrement sans eux ?`
      );
      if (!proceed) return;
    }
    setTitle(
      renewalMode
        ? `Renouvellement · ${renewalItems.reduce((s, r) => s + Number(r.qty || 0), 0)} unités · ${form.security_level} ${form.license_years}A`
        : solutionsOnlyMode
          ? `Solutions · ${addons.length} ligne(s) · ${form.security_level} ${form.license_years}A`
          : manualMode
            ? `Manuel · ${manualModels.length} modèle(s) · ${form.security_level} ${form.license_years}A`
            : `${form.customer_type} · ${form.users} util. · ${form.use_case}`
    );
    setSaveOpen(true);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const r = await api.post("/quotes", {
        title,
        configuration: result,
        customer_info: customerInfo,
      });
      toast.success("Devis enregistré");
      setSavedQuoteId(r.data?.id || null);
      setReviewRequested(!!r.data?.review_status);
      setAssignedSe(r.data?.assigned_se || null);
      setSaveOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const ensureQuoteSaved = async () => {
    // Returns the existing quote_id when already saved, otherwise creates
    // a quote silently with a sensible default title and returns its id.
    // Used by the « Demander une revue » and « Ouvrir Salon Fortinet » CTAs
    // so the partner doesn't have to manually save first.
    if (savedQuoteId) return savedQuoteId;
    try {
      const defaultTitle =
        title?.trim() ||
        (customerInfo?.name
          ? `${customerInfo.name} — ${result?.inputs?.use_case || "BOM"}`
          : `BOM ${new Date().toLocaleDateString("fr-FR")}`);
      const r = await api.post("/quotes", {
        title: defaultTitle,
        configuration: result,
        customer_info: customerInfo,
      });
      const newId = r.data?.id || null;
      setSavedQuoteId(newId);
      setReviewRequested(!!r.data?.review_status);
      setAssignedSe(r.data?.assigned_se || null);
      // Persist the title field so subsequent manual save dialogs are
      // pre-filled with the same value.
      if (!title) setTitle(defaultTitle);
      return newId;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'enregistrement automatique");
      return null;
    }
  };

  const requestReview = async (message) => {
    const qid = await ensureQuoteSaved();
    if (!qid) return;
    try {
      await api.post(`/quotes/${qid}/review-request`, {
        message: message || null,
      });
      setReviewRequested(true);
      toast.success("Demande de revue envoyée à l'équipe Fortinet.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la demande de revue");
    }
  };

  const openChatRoom = async () => {
    const qid = await ensureQuoteSaved();
    if (qid) setChatQuoteId(qid);
  };

  const assignSE = async (se) => {
    // se = null to clear, or { email, name, role } from waf-region.js
    const qid = await ensureQuoteSaved();
    if (!qid) return;
    try {
      const payload = se
        ? { se_email: se.email, se_name: se.name, se_role: se.role }
        : { se_email: null };
      const r = await api.post(`/quotes/${qid}/assign-se`, payload);
      setAssignedSe(r.data?.assigned_se || null);
      toast.success(se ? `SE alloué : ${se.name}` : "Allocation SE retirée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'enregistrer le SE");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
      <div className="flex items-start justify-between gap-4 mb-6 sm:mb-8 flex-wrap">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Générateur de BOM/BOQ</h1>
        {/* Compact context strip — shows who the partner is, who's logged in
            and the partnership tier for the BOM. Visible at all times so the
            commercial workflow is unambiguous (no surprise discounts). */}
        {user && (
          <div className="inline-flex flex-wrap items-stretch gap-0 rounded-sm border border-zinc-200 bg-white overflow-hidden" data-testid="configurator-context-strip">
            <ContextPill
              icon={Building2}
              label="Partenaire"
              value={(customerInfo?.partner_name || user.company || "—").trim() || "—"}
              accent="zinc"
              testid="ctx-partner-name"
            />
            <ContextPill
              icon={User}
              label="Utilisateur"
              value={user.full_name || user.email}
              subline={user.email}
              accent="zinc"
              testid="ctx-user"
            />
            <PartnerLevelPill
              level={user?.partner_level_locked && user?.partner_level
                ? user.partner_level
                : (form.partner_level === "none" ? null : form.partner_level)}
              locked={!!user?.partner_level_locked}
              testid="ctx-partner-level"
            />
          </div>
        )}
      </div>

      {/* Monthly freshness reminder for the bundled Fortinet price list */}
      <PricelistFreshnessBanner meta={baseMeta} />

      {/* Pricelist indicator */}
      {user && activePL && (
        <div className="mb-6 border border-zinc-200 bg-white rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid="configurator-pricelist-banner">
          <p className="text-sm text-zinc-700 truncate">
            <span className="font-medium">Surcharge active</span>
            <span className="text-zinc-500"> · {activePL.row_count} références personnalisées</span>
          </p>
          <Button asChild variant="outline" size="sm" className="rounded-sm border-zinc-300 shrink-0" data-testid="configurator-pricelist-link">
            <Link to="/pricelist">Gérer</Link>
          </Button>
        </div>
      )}

      {/* Customer / partner metadata captured up-front. Persisted with the
          saved quote so "Mes Devis" can later regenerate the Outlook draft
          adressed to the right Account Managers without re-asking.

          Acts as Step 1 of a progressive workflow: the configuration form
          below stays hidden until the partner fills in the client name and
          country (minimum viable context for any downstream BOM). */}
      {user && (
        <div className="mb-6">
          <CustomerInfoForm value={customerInfo} onChange={setCustomerInfo} user={user} />
        </div>
      )}

      {/* Workflow gate — waits for minimal customer context before revealing
          the rest. Keeps the page focused and avoids BOMs without attribution. */}
      {user && !(customerInfo.name && customerInfo.country) ? (
        <div
          className="border border-dashed border-zinc-300 bg-zinc-50 rounded-sm px-5 py-8 text-center"
          data-testid="configurator-locked"
        >
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-400">
            Étape 2 / 2 — Verrouillée
          </p>
          <p className="mt-2 text-sm font-medium text-zinc-700">
            Renseignez au minimum le <strong>nom</strong> et le <strong>pays</strong> du client
          </p>
          <p className="mt-1 text-xs text-zinc-500 max-w-md mx-auto">
            Le configurateur de BOM/BOQ s'ouvrira automatiquement dès que ces deux
            informations seront saisies dans la section ci-dessus. Cela garantit
            que chaque devis est rattaché à un client identifié.
          </p>
        </div>
      ) : (
      <>

      <form id="configurator-form" onSubmit={submit} className="border border-zinc-200 bg-white rounded-sm p-6" data-testid="configurator-form">
        {/* Mode toggle */}
        <div className="mb-6 flex flex-wrap items-center gap-2 pb-5 border-b border-zinc-100" data-testid="configurator-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("deployment")}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              mode === "deployment"
                ? "bg-zinc-900 text-white"
                : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"
            }`}
            data-testid="mode-new-deployment"
          >
            BOM Networking
          </button>
          <button
            type="button"
            onClick={() => setMode("renewal")}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              mode === "renewal"
                ? "bg-zinc-900 text-white"
                : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"
            }`}
            data-testid="mode-license-renewal"
          >
            BOM Renewal
          </button>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              mode === "manual"
                ? "bg-zinc-900 text-white"
                : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"
            }`}
            data-testid="mode-manual"
          >
            BOM Manuel
          </button>
          {/* BOM Fabric Fortinet — disabled, work-in-progress placeholder */}
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Fonctionnalité en cours de développement"
            className="relative px-4 py-2 rounded-sm text-sm font-medium bg-zinc-50 border border-zinc-200 text-zinc-400 cursor-not-allowed flex items-center gap-2"
            data-testid="mode-solutions-only-disabled"
          >
            <span className="line-through decoration-zinc-300">BOM Fabric Fortinet</span>
            <span className="text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
              En cours de développement
            </span>
          </button>
        </div>

        {/* Renewal-only panel */}
        {renewalMode && (
          <div className="mb-6">
            <RenewalPanel value={renewalItems} onChange={setRenewalItems} />
          </div>
        )}

        {/* Manual model picker */}
        {manualMode && (
          <div className="mb-6">
            <ManualModelsPanel value={manualModels} onChange={setManualModels} />
          </div>
        )}

        {/* Sizing method sub-toggle (BOM Networking only) */}
        {!renewalMode && !solutionsOnlyMode && !manualMode && (
          <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid="sizing-method-toggle">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">
              Méthode de dimensionnement :
            </span>
            <div
              className="inline-flex items-stretch rounded-sm border border-zinc-200 overflow-hidden bg-white"
              role="group"
              aria-label="Méthode de dimensionnement"
            >
              <button
                type="button"
                onClick={() => { setSizingMethod("users"); setPerfSizing({}); }}
                aria-pressed={sizingMethod === "users"}
                className={`px-4 py-1.5 text-xs font-semibold tracking-wide uppercase transition-colors ${
                  sizingMethod === "users"
                    ? "bg-[#D42B1E] text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="sizing-method-users"
              >
                Option Simple
              </button>
              <button
                type="button"
                onClick={() => {
                  setSizingMethod("performance");
                  // First-time hint: explain the 4 performance inputs in one
                  // line so partners new to the advanced mode know what to
                  // enter (idempotent — localStorage flag stops repeat).
                  try {
                    const KEY = "pyth.hint.perfSizingSeen";
                    if (!localStorage.getItem(KEY)) {
                      toast.info(
                        "Saisissez les seuils de votre trafic : le moteur sélectionne la plus petite FortiGate qui satisfait les 4 critères (Threat Protection, Sessions concurrentes, SSL Inspection, IPSec VPN).",
                        { duration: 8000 }
                      );
                      localStorage.setItem(KEY, "1");
                    }
                  } catch { /* localStorage unavailable — skip hint */ }
                }}
                aria-pressed={sizingMethod === "performance"}
                className={`px-4 py-1.5 text-xs font-semibold tracking-wide uppercase transition-colors border-l border-zinc-200 ${
                  sizingMethod === "performance"
                    ? "bg-[#D42B1E] text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="sizing-method-performance"
              >
                Option Avancée
              </button>
            </div>
            <span className="text-[11px] text-zinc-400 font-mono">
              {sizingMethod === "users"
                ? "Par nombre d'utilisateurs"
                : "Par charge réelle (TP · Sessions · SSL · IPSec)"}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {!renewalMode && !solutionsOnlyMode && !manualMode && sizingMethod === "users" && (
            <>
              <Field label="Nombre d'utilisateurs">
                <Input type="number" min={1} required value={form.users} onChange={(e) => update("users", e.target.value)} className="rounded-sm" data-testid="config-users-input" />
              </Field>
              <Field label="Nombre de sites">
                <Input type="number" min={1} required value={form.sites} onChange={(e) => update("sites", e.target.value)} className="rounded-sm" data-testid="config-sites-input" />
              </Field>
            </>
          )}
          {perfMode && (
            <>
              <Field label="Threat Protection (Gbps)">
                <Input type="number" min={0} step="0.1" placeholder="ex. 5"
                  value={perfSizing.threat_protection_gbps ?? ""}
                  onChange={(e) => setPerfSizing((p) => ({ ...p, threat_protection_gbps: e.target.value === "" ? null : Number(e.target.value) }))}
                  className="rounded-sm font-mono"
                  data-testid="perf-threat-protection-input" />
              </Field>
              <Field label="Sessions concurrentes">
                <Input type="number" min={0} placeholder="ex. 5000000"
                  value={perfSizing.concurrent_sessions ?? ""}
                  onChange={(e) => setPerfSizing((p) => ({ ...p, concurrent_sessions: e.target.value === "" ? null : Number(e.target.value) }))}
                  className="rounded-sm font-mono"
                  data-testid="perf-concurrent-sessions-input" />
              </Field>
              <Field label="SSL Inspection (Gbps)">
                <Input type="number" min={0} step="0.1" placeholder="ex. 4"
                  value={perfSizing.ssl_inspection_gbps ?? ""}
                  onChange={(e) => setPerfSizing((p) => ({ ...p, ssl_inspection_gbps: e.target.value === "" ? null : Number(e.target.value) }))}
                  className="rounded-sm font-mono"
                  data-testid="perf-ssl-inspection-input" />
              </Field>
              <Field label="IPSec VPN (Gbps)">
                <Input type="number" min={0} step="0.1" placeholder="ex. 10"
                  value={perfSizing.ipsec_vpn_gbps ?? ""}
                  onChange={(e) => setPerfSizing((p) => ({ ...p, ipsec_vpn_gbps: e.target.value === "" ? null : Number(e.target.value) }))}
                  className="rounded-sm font-mono"
                  data-testid="perf-ipsec-vpn-input" />
              </Field>
              <Field label="Nombre de sites">
                <Input type="number" min={1} required value={form.sites}
                  onChange={(e) => update("sites", e.target.value)}
                  className="rounded-sm" data-testid="config-sites-input" />
              </Field>
            </>
          )}
          {!solutionsOnlyMode && !manualMode && (
            <Field label="Type de client">
              <Select value={form.customer_type} onValueChange={(v) => update("customer_type", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CUSTOMER_TYPES.map((c) => <SelectItem key={c} value={c} data-testid={`customer-type-${c}`}>{c === "SMB" ? "PME" : c === "Government" ? "Secteur Public" : c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {!renewalMode && !solutionsOnlyMode && !manualMode && (
            <Field label="Type de déploiement">
              <Select value={form.deployment_type} onValueChange={(v) => update("deployment_type", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-deployment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical" data-testid="deployment-type-physical">Appliance physique</SelectItem>
                  <SelectItem value="vm" data-testid="deployment-type-vm">FortiGate VM (virtuel)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
          {!renewalMode && !solutionsOnlyMode && !manualMode && (
            <Field label="Cas d'usage">
              <Select value={form.use_case} onValueChange={(v) => update("use_case", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-use-case-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USE_CASES.map((c) => <SelectItem key={c} value={c} data-testid={`use-case-${c}`}>{c === "Firewall" ? "Pare-feu" : c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {!solutionsOnlyMode && (
            <Field label={renewalMode ? "Niveau de licence à renouveler" : "Niveau de sécurité"}>
              <Select value={form.security_level} onValueChange={(v) => update("security_level", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-security-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECURITY_LEVELS.map((c) => <SelectItem key={c} value={c} data-testid={`security-level-${c}`}>{c === "Basic" ? "ATP (Basique)" : c === "Advanced" ? "UTP (Avancée)" : "Enterprise (Full Protection)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {!solutionsOnlyMode && (
            <Field label={renewalMode ? "Durée du renouvellement" : "Durée de licence"}>
              <Select value={String(form.license_years)} onValueChange={(v) => update("license_years", Number(v))}>
                <SelectTrigger className="rounded-sm" data-testid="config-license-years-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((c) => <SelectItem key={c} value={String(c)} data-testid={`license-years-${c}`}>{c} an{c > 1 ? "s" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Niveau de partenariat">
            {user?.partner_level_locked && user?.partner_level ? (
              <div
                className="h-9 px-3 flex items-center justify-between gap-2 rounded-sm border border-zinc-300 bg-zinc-50 text-sm"
                data-testid="config-partner-level-locked"
                title="Niveau partenaire figé par l'administrateur"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm font-mono font-semibold bg-[#D42B1E] text-white">
                    🔒 {user.partner_level}
                  </span>
                  {(() => {
                    const p = PARTNER_LEVELS.find((x) => x.value === user.partner_level);
                    return p ? (
                      <span className="text-xs text-zinc-500 font-mono">
                        HW −{p.hw}% · SW −{p.sw}%
                      </span>
                    ) : null;
                  })()}
                </span>
                <span className="text-[10px] text-zinc-400 uppercase tracking-wide">Figé</span>
              </div>
            ) : (
              <Select value={form.partner_level} onValueChange={(v) => update("partner_level", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-partner-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARTNER_LEVELS.map((p) => (
                    <SelectItem key={p.value} value={p.value} data-testid={`partner-level-${p.value}`}>
                      {p.label}
                      {p.hw ? <span className="ml-2 text-xs text-zinc-500 font-mono">HW -{p.hw}% · SW -{p.sw}%</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {!renewalMode && (
            <Field label="Transporteur">
              <Select value={form.carrier} onValueChange={(v) => update("carrier", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARRIERS.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`carrier-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {!renewalMode && form.carrier !== "none" && (
            <Field label="Zone de destination">
              <Select value={form.destination_zone} onValueChange={(v) => update("destination_zone", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-zone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DESTINATION_ZONES.map((z) => (
                    <SelectItem key={z.value} value={z.value} data-testid={`zone-${z.value}`}>
                      {z.label}<span className="ml-2 text-xs text-zinc-500 font-mono">×{z.mult}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
      </form>

      {!renewalMode && !solutionsOnlyMode && !manualMode && (
        <>
          <NetworkingAccessPanel
            value={addons}
            onChange={setAddons}
            users={form.users}
            sites={form.sites}
          />
          <AddonsPanel
            value={addons}
            onChange={setAddons}
            useCase={form.use_case}
            securityLevel={form.security_level}
            licenseYears={form.license_years}
            allowedSolutionIds={["fortinac", "fortiauthenticator"]}
            hiddenSolutionIds={["fortiswitch", "fortiap"]}
          />
          <div className="mt-6">
            <SfpPanel
              hqModel={form.users <= 50 ? "FG-40F" : form.users <= 100 ? "FG-60F" : form.users <= 250 ? "FG-80F" : form.users <= 500 ? "FG-100F" : form.users <= 1000 ? "FG-200F" : form.users <= 2500 ? "FG-400F" : "FG-600F"}
              value={sfpQuantities}
              onChange={setSfpQuantities}
              useCase={form.use_case}
              users={form.users}
              sites={form.sites}
            />
          </div>
        </>
      )}

      {manualMode && (
        <>
          <AddonsPanel
            value={addons}
            onChange={setAddons}
            useCase={null}
            securityLevel={form.security_level}
            licenseYears={form.license_years}
          />
          {manualModels.length > 0 && (
            <div className="mt-6">
              <SfpPanel
                hqModel={manualModels[0].model}
                value={sfpQuantities}
                onChange={setSfpQuantities}
                useCase="Firewall"
                users={1}
                sites={1}
              />
            </div>
          )}
        </>
      )}

      {solutionsOnlyMode && (
        <AddonsPanel
          value={addons}
          onChange={setAddons}
          useCase={null}
          securityLevel={form.security_level}
          licenseYears={form.license_years}
        />
      )}
      </>
      )}
      {/* END of workflow gate */}

      {/* Action bar — sticky to the viewport bottom AND placed at the very
          end of the page so the Generate / Reset buttons always remain
          visible AND below the freshly generated BOM (rather than floating
          mid-page once results render). */}

      {result && (
        <div id="results-section" className="mt-10">
          <ResultsView
            result={result}
            customerInfo={customerInfo}
            onSave={openSave}
            saving={saving}
            savedQuoteId={savedQuoteId}
            reviewRequested={reviewRequested}
            assignedSe={assignedSe}
            onRequestReview={requestReview}
            onOpenChatRoom={openChatRoom}
            onAssignSE={assignSE}
            pendingAddons={addons}
            onAddAddon={(line) => {
              setAddons((prev) =>
                prev.some((p) => p.sku === line.sku)
                  ? prev.map((p) => (p.sku === line.sku ? { ...p, quantity: Number(p.quantity) + 1 } : p))
                  : [...prev, line]
              );
              // Scroll back up to the addons panel so user sees the new line
              document.querySelector('[data-testid="addons-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </div>
      )}

      <div
        className="mt-8 sticky bottom-3 sm:bottom-4 z-20 border border-zinc-200 bg-white/95 backdrop-blur shadow-lg rounded-sm p-3 sm:p-5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3"
        data-testid="configurator-action-bar"
      >
        <p className="text-xs text-zinc-500 font-mono leading-relaxed line-clamp-2 sm:line-clamp-none flex-1 min-w-0">
          {renewalMode
            ? `${renewalItems.reduce((s, r) => s + Number(r.qty || 0), 0)} unité(s) à renouveler · ${renewalItems.length} modèle(s)`
            : solutionsOnlyMode
              ? `${addons.length} solution(s) · ${addons.reduce((s, a) => s + Number(a.quantity || 0), 0)} unité(s)`
              : manualMode
                ? `${manualModels.length} modèle(s) · ${manualModels.reduce((s, m) => s + Number(m.quantity || 0), 0)} unité(s)`
                : perfMode
                  ? `Dimensionnement par charge · ${[
                      perfSizing.threat_protection_gbps && `${perfSizing.threat_protection_gbps} Gbps TP`,
                      perfSizing.concurrent_sessions && `${(perfSizing.concurrent_sessions / 1e6).toFixed(1)}M sessions`,
                      perfSizing.ssl_inspection_gbps && `${perfSizing.ssl_inspection_gbps} Gbps SSL`,
                      perfSizing.ipsec_vpn_gbps && `${perfSizing.ipsec_vpn_gbps} Gbps IPSec`,
                    ].filter(Boolean).join(" · ") || "exigences à préciser"}`
                  : `Dimensionnement : ${form.users} util. → ${form.users <= 50 ? "FG-40F" : form.users <= 100 ? "FG-60F" : form.users <= 250 ? "FG-80F" : form.users <= 500 ? "FG-100F" : form.users <= 1000 ? "FG-200F" : form.users <= 2500 ? "FG-400F" : "FG-600F"}`}
        </p>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            type="submit"
            form="configurator-form"
            disabled={loading}
            className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11 px-4 sm:px-6 flex-1 sm:flex-none text-sm"
            data-testid="config-generate-button"
          >
            {loading ? "Génération..." : renewalMode ? "Générer renouvellement" : solutionsOnlyMode ? "Générer Solutions" : manualMode ? "Générer manuel" : "Générer BOM / BOQ"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={resetCurrentMode}
            className="rounded-sm h-11 px-3 sm:px-4 border-zinc-300 text-zinc-700 hover:bg-zinc-50 shrink-0"
            data-testid="config-reset-button"
            title="Effacer ce BOM et ses saisies (l'enregistrement éventuel reste dans Mes devis)"
          >
            Réinitialiser
          </Button>
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-sm" data-testid="save-quote-dialog">
          <DialogHeader>
            <DialogTitle>Enregistrer le devis</DialogTitle>
            <DialogDescription>Nommez ce devis pour le retrouver plus tard.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-sm" data-testid="save-quote-title-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} className="rounded-sm" data-testid="save-quote-cancel-button">Annuler</Button>
            <Button onClick={doSave} disabled={saving || !title} className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm" data-testid="save-quote-confirm-button">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salon Fortinet — chat sheet attached to the auto-saved quote.
          Opened by the « Ouvrir Salon Fortinet » CTA in ResultsView. */}
      <Sheet open={!!chatQuoteId} onOpenChange={(v) => !v && setChatQuoteId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] p-0 flex flex-col border-l-0 sm:border-l-4 sm:border-l-[#D42B1E]"
          data-testid="configurator-chat-sheet"
        >
          <SheetTitle className="sr-only">Salon Fortinet</SheetTitle>
          {chatQuoteId && (
            <QuoteChatRoom
              quoteId={chatQuoteId}
              onReviewStatusChange={() => setReviewRequested(true)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


/**
 * Compact context pill — used in the configurator header strip to surface
 * the partner / user / partnership level at a glance.
 */
function ContextPill({ icon: Icon, label, value, subline, accent = "zinc", testid }) {
  return (
    <div
      className="px-3 sm:px-4 py-2 border-r last:border-r-0 border-zinc-200 flex items-center gap-2 min-w-0"
      data-testid={testid}
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${accent === "rouge" ? "text-[#D42B1E]" : "text-zinc-400"}`} />
      <div className="min-w-0">
        <p className="text-[9px] font-semibold tracking-[0.16em] uppercase text-zinc-400 leading-none">
          {label}
        </p>
        <p className="text-xs font-semibold text-zinc-900 truncate max-w-[180px]" title={value}>
          {value || "—"}
        </p>
        {subline && (
          <p className="text-[10px] font-mono text-zinc-400 truncate max-w-[180px]" title={subline}>
            {subline}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Partner-level pill with colour coding — Advocate / Select / Advanced /
 * Expert (Fortinet's official tiers). Padlock icon when the level is locked
 * by an admin (`partner_level_locked = true`) to make it obvious that the
 * user can't override it via the inline picker.
 */
function PartnerLevelPill({ level, locked, testid }) {
  const map = {
    Advocate: { bg: "bg-zinc-100",  text: "text-zinc-700",   label: "Advocate" },
    Select:   { bg: "bg-sky-100",   text: "text-sky-700",    label: "Select" },
    Advanced: { bg: "bg-amber-100", text: "text-amber-800",  label: "Advanced" },
    Expert:   { bg: "bg-emerald-100", text: "text-emerald-700", label: "Expert" },
  };
  const meta = level ? map[level] : null;
  return (
    <div className="px-3 sm:px-4 py-2 flex items-center gap-2 min-w-0" data-testid={testid}>
      <Award className="h-3.5 w-3.5 text-[#D42B1E] shrink-0" />
      <div className="min-w-0">
        <p className="text-[9px] font-semibold tracking-[0.16em] uppercase text-zinc-400 leading-none flex items-center gap-1">
          Niveau partenaire
          {locked && <Lock className="h-2.5 w-2.5 text-zinc-400" title="Niveau verrouillé par l'administrateur" />}
        </p>
        {meta ? (
          <span className={`mt-0.5 inline-block px-1.5 py-0.5 rounded-sm text-[11px] font-semibold ${meta.bg} ${meta.text}`}>
            {meta.label}
          </span>
        ) : (
          <span className="mt-0.5 inline-block text-[11px] text-zinc-500 italic">
            Non défini
          </span>
        )}
      </div>
    </div>
  );
}
